import React, { useState, useEffect } from 'react';
import { Card, Typography, Table, Button, Input, Space, Tag, Modal, Form, Spin, Alert } from 'antd';
import { SearchOutlined, ImportOutlined, EyeOutlined } from '@ant-design/icons';
import { useAuthStore } from '@stores/authStore';

const { Title, Paragraph } = Typography;

interface Provider {
  id: string;
  npi: string;
  provider_type: string;
  first_name: string | null;
  last_name: string | null;
  organization_name: string | null;
  specialty_primary: string | null;
  city: string | null;
  state: string | null;
  phone: string | null;
  email: string | null;
  medicare_enrollment_status: string | null;
}

const Providers: React.FC = () => {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [importForm] = Form.useForm();
  const [authChecked, setAuthChecked] = useState(false);

  // Get token from store
  const token = useAuthStore((state) => state.token);

  useEffect(() => {
    console.log('[Providers] Component mounted');
    console.log('[Providers] Token:', token ? 'PRESENT' : 'MISSING');
    
    if (!token) {
      console.log('[Providers] No token, will retry in 1s...');
      const timer = setTimeout(() => {
        setAuthChecked(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
    
    setAuthChecked(true);
    loadProviders();
  }, [token]);

  const loadProviders = async () => {
    const currentToken = useAuthStore.getState().token;
    console.log('[Providers] loadProviders called with token:', currentToken ? 'YES' : 'NO');
    
    if (!currentToken) {
      setError('Not authenticated. Please log in.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setProviders([]);

      const apiUrl = 'https://aethera-crm-api.aetherahealthcare.workers.dev/api/v1/providers?page=1&per_page=20';
      console.log('[Providers] Fetching:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('[Providers] Response status:', response.status);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('[Providers] Response:', JSON.stringify(result, null, 2));

      let providerList: Provider[] = [];
      let totalCount = 0;

      if (result.data && Array.isArray(result.data)) {
        providerList = result.data;
        totalCount = result.pagination?.total || result.data.length;
      } else if (Array.isArray(result)) {
        providerList = result;
        totalCount = result.length;
      } else {
        console.error('[Providers] Unexpected structure:', result);
        setError('API returned unexpected structure');
        setLoading(false);
        return;
      }

      console.log('[Providers] Loaded', providerList.length, 'providers, total:', totalCount);

      setProviders(providerList);
      setTotal(totalCount);
    } catch (err: any) {
      console.error('[Providers] Error:', err);
      setError(err.message || 'Failed to load providers');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Provider Name',
      key: 'name',
      render: (record: Provider) => {
        const name = record.provider_type === 'individual'
          ? `Dr. ${record.first_name || ''} ${record.last_name || ''}`.trim()
          : record.organization_name || 'Unknown';
        return (
          <div>
            <div style={{ fontWeight: 'bold' }}>{name}</div>
            {record.specialty_primary && (
              <div style={{ fontSize: '12px', color: '#666' }}>{record.specialty_primary}</div>
            )}
          </div>
        );
      },
    },
    {
      title: 'NPI',
      dataIndex: 'npi',
      key: 'npi',
      render: (npi: string) => <Tag>{npi || 'N/A'}</Tag>,
    },
    {
      title: 'Location',
      key: 'location',
      render: (record: Provider) =>
        `${record.city || ''}, ${record.state || ''}`.trim() || '-',
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone: string | null) => phone || '-',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (email: string | null) => {
        if (!email) return <span style={{ color: '#999' }}>Not available</span>;
        return email;
      },
    },
    {
      title: 'Medicare',
      dataIndex: 'medicare_enrollment_status',
      key: 'medicare',
      render: (status: string | null) => {
        if (!status) return <Tag>Unknown</Tag>;
        return <Tag color={status === 'active' ? 'green' : 'default'}>{status}</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: Provider) => (
        <Space>
          <Button type="link" icon={<EyeOutlined />} onClick={() => console.log('View:', record.npi)}>
            View
          </Button>
        </Space>
      ),
    },
  ];

  if (!authChecked || loading) {
    return (
      <div className="page-container" style={{ textAlign: 'center', padding: '100px' }}>
        <Spin size="large" />
        <p>Loading providers...</p>
        {!token && <p>Waiting for authentication...</p>}
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container" style={{ padding: '50px' }}>
        <Alert
          message="Error Loading Providers"
          description={error}
          type="error"
          showIcon
          action={
            <Button type="primary" onClick={loadProviders}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <Title level={2}>Providers</Title>
        <Paragraph>Healthcare provider directory with NPPES integration</Paragraph>
      </div>

      <Card className="hover-card" style={{ marginBottom: '24px' }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Input
            placeholder="Search by NPI, name, or specialty"
            prefix={<SearchOutlined />}
            style={{ width: '400px' }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <Button
            type="primary"
            icon={<ImportOutlined />}
            onClick={() => setModalVisible(true)}
          >
            Import from NPPES
          </Button>
        </Space>

        <div style={{ marginTop: '16px', fontSize: '14px', color: '#666' }}>
          Showing <strong>{providers.length}</strong> of <strong>{total}</strong> providers
          {!loading && providers.length > 0 && (
            <span style={{ marginLeft: '16px', color: '#52c41a' }}>✅ Data loaded</span>
          )}
        </div>
      </Card>

      <Card className="hover-card">
        <Table
          columns={columns}
          dataSource={providers}
          rowKey="npi"
          pagination={{
            pageSize: 20,
            current: 1,
            total: total,
          }}
          locale={{
            emptyText: 'No providers found.',
          }}
        />
      </Card>

      <Modal
        title="Import Provider from NPPES"
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          importForm.resetFields();
        }}
        footer={null}
      >
        <Form form={importForm} layout="vertical">
          <Form.Item label="NPI Number" rules={[{ required: true }]}>
            <Input placeholder="Enter 10-digit NPI number" />
          </Form.Item>
          <Button type="primary" block>Import Provider</Button>
        </Form>
      </Modal>
    </div>
  );
};

export default Providers;